import React, { useState, useEffect } from 'react';
import { Phone, Mail, TrendingUp, Calendar } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

interface NewContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contractData: ContractData) => Promise<void>;
  renewalData?: {
    type: 'cold_calling' | 'mailing' | 'growth';
    billing_unit: 'days' | 'campaign' | 'package';
    days_per_month: number | null;
    monthly_price: number | null;
    notes: string | null;
  } | null;
}

export interface ContractData {
  type: 'cold_calling' | 'mailing' | 'growth';
  billing_unit: 'days' | 'campaign' | 'package';
  signed_date?: string;
  start_date: string;
  end_date?: string;
  days_per_month?: number;
  monthly_price?: number;
  notes?: string;
}

const NewContractModal: React.FC<NewContractModalProps> = ({
  isOpen,
  onClose,
  onSave,
  renewalData,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ContractData>({
    type: 'cold_calling',
    billing_unit: 'days',
    start_date: '',
  });

  useEffect(() => {
    if (renewalData) {
      setFormData({
        type: renewalData.type,
        billing_unit: renewalData.billing_unit,
        start_date: '',
        days_per_month: renewalData.days_per_month || undefined,
        monthly_price: renewalData.monthly_price || undefined,
        notes: renewalData.notes || undefined,
      });
    } else {
      setFormData({
        type: 'cold_calling',
        billing_unit: 'days',
        start_date: '',
      });
    }
  }, [renewalData, isOpen]);

  const handleChange = (field: keyof ContractData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTypeChange = (type: 'cold_calling' | 'mailing' | 'growth') => {
    let billing_unit: 'days' | 'campaign' | 'package' = 'days';
    if (type === 'mailing') billing_unit = 'campaign';
    if (type === 'growth') billing_unit = 'package';

    setFormData((prev) => ({
      type,
      billing_unit,
      start_date: prev.start_date,
      signed_date: prev.signed_date,
      end_date: prev.end_date,
      notes: prev.notes,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.start_date) {
      alert('Veuillez indiquer la date de début');
      return;
    }

    if (formData.type === 'cold_calling') {
      if (!formData.signed_date || !formData.days_per_month || !formData.monthly_price) {
        alert('Veuillez remplir tous les champs obligatoires pour Cold Calling');
        return;
      }
    }

    if (formData.type === 'mailing' && !formData.monthly_price) {
      alert('Veuillez indiquer le prix mensuel');
      return;
    }

    if (formData.type === 'growth' && !formData.monthly_price) {
      alert('Veuillez indiquer le prix du package');
      return;
    }

    try {
      setLoading(true);
      await onSave(formData);
      setFormData({
        type: 'cold_calling',
        billing_unit: 'days',
        start_date: '',
      });
      onClose();
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Erreur lors de la sauvegarde du contrat');
    } finally {
      setLoading(false);
    }
  };

  const pricePerDay =
    formData.type === 'cold_calling' &&
    formData.monthly_price &&
    formData.days_per_month &&
    formData.days_per_month > 0
      ? (formData.monthly_price / formData.days_per_month).toFixed(2)
      : '0.00';

  const typeButtons = [
    { value: 'cold_calling' as const, label: 'Cold Calling', icon: Phone, emoji: '📞' },
    { value: 'mailing' as const, label: 'Mailing', icon: Mail, emoji: '📧' },
    { value: 'growth' as const, label: 'Growth', icon: TrendingUp, emoji: '🚀' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={renewalData ? "Renouveler le contrat" : "Nouveau contrat"}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {!renewalData && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Type de contrat *
            </label>
            <div className="grid grid-cols-3 gap-3">
              {typeButtons.map((type) => {
                const Icon = type.icon;
                const isSelected = formData.type === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleTypeChange(type.value)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-[#E91E8C] bg-pink-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl">{type.emoji}</span>
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-[#E91E8C]' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${isSelected ? 'text-[#E91E8C]' : 'text-gray-700'}`}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {formData.type === 'cold_calling' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Date signature *
                </label>
                <input
                  type="date"
                  value={formData.signed_date || ''}
                  onChange={(e) => handleChange('signed_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Mois de début *
                </label>
                <input
                  type="month"
                  value={formData.start_date ? formData.start_date.substring(0, 7) : ''}
                  onChange={(e) => handleChange('start_date', e.target.value + '-01')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Mois de fin *
                </label>
                <input
                  type="month"
                  value={formData.end_date ? formData.end_date.substring(0, 7) : ''}
                  onChange={(e) => {
                    const [year, month] = e.target.value.split('-');
                    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
                    handleChange('end_date', `${year}-${month}-${lastDay.toString().padStart(2, '0')}`);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jours/mois *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.days_per_month || ''}
                  onChange={(e) => handleChange('days_per_month', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix mensuel (€) *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.monthly_price || ''}
                    onChange={(e) => handleChange('monthly_price', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                    required
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500 text-sm">€/mois</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Prix par jour (calculé)</span>
                <span className="text-lg font-bold text-[#E91E8C]">{pricePerDay} €/jour</span>
              </div>
            </div>
          </>
        )}

        {formData.type === 'mailing' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Date signature *
                </label>
                <input
                  type="date"
                  value={formData.signed_date || ''}
                  onChange={(e) => handleChange('signed_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Mois de début *
                </label>
                <input
                  type="month"
                  value={formData.start_date ? formData.start_date.substring(0, 7) : ''}
                  onChange={(e) => handleChange('start_date', e.target.value + '-01')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Mois de fin *
                </label>
                <input
                  type="month"
                  value={formData.end_date ? formData.end_date.substring(0, 7) : ''}
                  onChange={(e) => {
                    const [year, month] = e.target.value.split('-');
                    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
                    handleChange('end_date', `${year}-${month}-${lastDay.toString().padStart(2, '0')}`);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prix mensuel (€) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.monthly_price || ''}
                  onChange={(e) => handleChange('monthly_price', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
                <span className="absolute right-3 top-2.5 text-gray-500 text-sm">€/mois</span>
              </div>
            </div>
          </>
        )}

        {formData.type === 'growth' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Mois de début *
                </label>
                <input
                  type="month"
                  value={formData.start_date ? formData.start_date.substring(0, 7) : ''}
                  onChange={(e) => handleChange('start_date', e.target.value + '-01')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Mois de fin (optionnel)
                </label>
                <input
                  type="month"
                  value={formData.end_date ? formData.end_date.substring(0, 7) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const [year, month] = e.target.value.split('-');
                      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
                      handleChange('end_date', `${year}-${month}-${lastDay.toString().padStart(2, '0')}`);
                    } else {
                      handleChange('end_date', '');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prix package (€) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.monthly_price || ''}
                  onChange={(e) => handleChange('monthly_price', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                  required
                />
                <span className="absolute right-3 top-2.5 text-gray-500 text-sm">€</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                placeholder="Détails du package growth..."
              />
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer le contrat'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default NewContractModal;
